import React, { useEffect, useMemo, useState } from "react";
import { Sparkles, ShieldAlert, Lightbulb, Compass, ChevronRight, Cpu, Database } from "lucide-react";
import { useTranslation } from "@/lib/i18n";

/**
 * Portfolio AI Review — Phase 8.A
 * --------------------------------
 * Renders the structured v2 portfolio review with sections:
 *   - Summary
 *   - Strengths / Weaknesses (parallel columns)
 *   - Concentration Risks (objects with severity pill)
 *   - Top Opportunity / Largest Valuation Risk (ticker + explanation)
 *   - Research Priorities
 *   - Confidence + meta (generated_at, source badge)
 *
 * Source badge:
 *   - "Portfolio Intelligence · AI Assisted"   when source = "llm"
 *   - "Portfolio Intelligence · Deterministic" when source = "deterministic_fallback"
 *
 * Loading state: rotating playful copy every 2.5s while `loading` is true.
 */

const ROTATING_LOADER_KEYS = [
  "portfolio.loader.running_value_lens",
  "portfolio.loader.scanning_value_traps",
  "portfolio.loader.checking_carriers",
  "portfolio.loader.stress_testing",
  "portfolio.loader.price_vs_value",
  "portfolio.loader.separating_signal",
];

const SEVERITY_TONE = {
  Low:    { color: "#2BFF8A", bg: "rgba(43,255,138,0.10)",  border: "rgba(43,255,138,0.45)"  },
  Medium: { color: "#FFB72B", bg: "rgba(255,183,43,0.10)",  border: "rgba(255,183,43,0.45)"  },
  High:   { color: "#FF3DA6", bg: "rgba(255,61,166,0.10)",  border: "rgba(255,61,166,0.45)"  },
};

function SeverityPill({ severity, t }) {
  const tone = SEVERITY_TONE[severity] || SEVERITY_TONE.Medium;
  const sevKey = String(severity || "Medium").toLowerCase();
  // Phase 8.B QA-4 — risk severity labels follow the active locale.
  const localizedMap = {
    low: (t && t("portfolio.risk.low")) || "Low",
    medium: (t && t("portfolio.risk.medium")) || "Medium",
    high: (t && t("portfolio.risk.high")) || "High",
  };
  const label = localizedMap[sevKey] || severity || localizedMap.medium;
  return (
    <span
      className="inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider"
      style={{ background: tone.bg, color: tone.color, border: `1px solid ${tone.border}` }}
      data-testid={`severity-pill-${sevKey}`}
    >
      {label}
    </span>
  );
}

function SectionHeader({ icon: Icon, color, label, testId }) {
  return (
    <div className="text-[11px] uppercase tracking-wider mb-2 flex items-center gap-1.5" style={{ color }} data-testid={testId}>
      {Icon ? <Icon className="w-3 h-3" /> : null}
      <span>{label}</span>
    </div>
  );
}

// Phase 8.B QA-10 — normalize any locale-ish value to the canonical
// two-letter codes the rest of the system uses. Anything that *starts
// with* "de" (e.g. "de", "DE", "de-DE", "de_AT") collapses to "de";
// every other non-empty value collapses to "en". Empty / null inputs
// return null so callers can distinguish "no info" from "matches".
function _normalizeLocale(value) {
  if (value == null) return null;
  const s = String(value).trim().toLowerCase();
  if (!s) return null;
  return s.startsWith("de") ? "de" : "en";
}

export default function PortfolioAIReview({ review, generatedAt, loading = false, cached = false, reviewMeta = null, previewMode = false }) {
  const { t, locale: uiLocale } = useTranslation();

  // Phase 8.B QA-10 — locale-mismatch is computed CLIENT-SIDE off the
  // persisted review locale and the currently-active UI locale. We do
  // NOT rely on `reviewMeta.locale_mismatch` (which is computed at
  // request time from the header-derived interface locale and goes
  // stale the moment the user toggles the language switcher without a
  // refetch). The persisted review locale is sourced in this order:
  //   1. `reviewMeta.review_locale`               (canonical, server-tagged)
  //   2. `review.review_locale`                   (embedded in the review blob)
  //   3. `null`                                   (unknown — never show banner)
  // The banner fires whenever a non-null normalized review locale
  // differs from the normalized UI locale. Cached / fresh / reloaded
  // reviews are all treated identically — there is no `cached` gate.
  const normalizedUiLocale = _normalizeLocale(uiLocale);
  const persistedReviewLocale =
    (reviewMeta && reviewMeta.review_locale) || (review && review.review_locale) || null;
  const normalizedReviewLocale = _normalizeLocale(persistedReviewLocale);
  const localeMismatch = Boolean(
    normalizedReviewLocale && normalizedUiLocale && normalizedReviewLocale !== normalizedUiLocale,
  );

  // Rotating loader copy.
  const [loaderIdx, setLoaderIdx] = useState(0);
  useEffect(() => {
    if (!loading) return undefined;
    const id = setInterval(
      () => setLoaderIdx((i) => (i + 1) % ROTATING_LOADER_KEYS.length),
      2500,
    );
    return () => clearInterval(id);
  }, [loading]);

  const isLLM = review?.source === "llm";
  const isDeterministic = review?.source === "deterministic_fallback";
  const badgeLabel = useMemo(() => {
    if (isLLM) return t("portfolio.badge.intelligence_ai_assisted") || "Portfolio Intelligence · AI Assisted";
    if (isDeterministic) return t("portfolio.badge.intelligence_deterministic") || "Portfolio Intelligence · Deterministic";
    return t("portfolio.badge.intelligence") || "Portfolio Intelligence · AI";
  }, [isLLM, isDeterministic, t]);
  const badgeIcon = isDeterministic ? Database : Cpu;
  const BadgeIcon = badgeIcon;

  // Loading state.
  if (loading) {
    return (
      <div
        data-testid="portfolio-ai-review-loading"
        className="relative overflow-hidden rounded-2xl border border-white/10 bg-[rgba(18,18,23,0.92)] shadow-[var(--app-shadow-elev-1)] p-5 sm:p-6"
      >
        <div className="absolute top-0 left-6 right-6 h-[2px] rounded-full bg-[#3DEBFF] shadow-[0_0_12px_rgba(61,235,255,0.45)]" />
        <div className="flex items-center gap-2 mb-3">
          <Sparkles className="w-4 h-4 text-[#3DEBFF] animate-pulse" />
          <h2 className="font-display text-lg">{t("portfolio.review.title") || "AI Portfolio Review"}</h2>
        </div>
        <p className="text-sm text-foreground/80 animate-pulse" data-testid="portfolio-ai-review-loader-copy">
          {t(ROTATING_LOADER_KEYS[loaderIdx]) || ""}
        </p>
        <div className="mt-4 space-y-2">
          <div className="h-3 w-3/4 rounded bg-white/5 animate-pulse" />
          <div className="h-3 w-full rounded bg-white/5 animate-pulse" />
          <div className="h-3 w-2/3 rounded bg-white/5 animate-pulse" />
        </div>
      </div>
    );
  }

  // No review yet — hide entirely so parent can show its own empty/CTA state.
  if (!review) return null;

  const strengths = Array.isArray(review.strengths) ? review.strengths : [];
  const weaknesses = Array.isArray(review.weaknesses)
    ? review.weaknesses
    : Array.isArray(review.risks)
      ? review.risks
      : [];
  const concentrationRisks = Array.isArray(review.concentration_risks) ? review.concentration_risks : [];
  const researchPriorities = Array.isArray(review.research_priorities) ? review.research_priorities : [];
  const topOpportunity = review.top_opportunity || null;
  const largestRisk = review.largest_valuation_risk || null;

  return (
    <div
      data-testid="portfolio-ai-review-block"
      className="relative overflow-hidden rounded-2xl border border-white/10 bg-[rgba(18,18,23,0.92)] shadow-[var(--app-shadow-elev-1)] p-5 sm:p-6"
    >
      <div className="absolute top-0 left-6 right-6 h-[2px] rounded-full bg-[#3DEBFF] shadow-[0_0_12px_rgba(61,235,255,0.45)]" />

      {/* Header row */}
      <div className="flex items-start justify-between gap-3 mb-3 flex-wrap">
        <div className="flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-[#3DEBFF]" />
          <h2 className="font-display text-lg">{t("portfolio.review.title") || "AI Portfolio Review"}</h2>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <span
            className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-medium border"
            style={{
              background: isDeterministic ? "rgba(255,183,43,0.10)" : "rgba(61,235,255,0.10)",
              color: isDeterministic ? "#FFB72B" : "#3DEBFF",
              borderColor: isDeterministic ? "rgba(255,183,43,0.45)" : "rgba(61,235,255,0.45)",
            }}
            data-testid="portfolio-ai-review-badge"
          >
            <BadgeIcon className="w-3 h-3" />
            {badgeLabel}
          </span>
          {cached ? (
            <span
              className="rounded-full px-2 py-0.5 text-[10px] font-medium border border-white/10 text-foreground/60"
              data-testid="portfolio-ai-review-cached"
            >
              {t("portfolio.freshness.cached")}
            </span>
          ) : null}
          {/* Phase 8.PH — 4-state AI Review status pill. Rendered from
              `reviewMeta.ai_review_status` (backend priority-ordered:
              portfolio_changed > market_data_unavailable > stale > up_to_date).
              This replaces the previous ad-hoc boolean pills so the UI
              always reflects a single, unambiguous state. */}
          {reviewMeta && reviewMeta.ai_review_status === "up_to_date" && reviewMeta.cached ? (
            <span
              className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-medium border border-[#2BFF8A]/40 bg-[rgba(43,255,138,0.06)] text-[#2BFF8A]"
              data-testid="portfolio-ai-review-status-up-to-date"
              aria-label={t("portfolio.freshness.status_up_to_date")}
            >
              <span className="h-1.5 w-1.5 rounded-full bg-[#2BFF8A]" />
              {t("portfolio.freshness.status_up_to_date")}
            </span>
          ) : null}
          {reviewMeta && reviewMeta.ai_review_status === "data_temporarily_stale" ? (
            <span
              className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-medium border border-[#FFB72B]/40 bg-[rgba(255,183,43,0.08)] text-[#FFB72B]"
              data-testid="portfolio-ai-review-status-data-temporarily-stale"
              aria-label={t("portfolio.freshness.status_data_temporarily_stale")}
            >
              <span className="h-1.5 w-1.5 rounded-full bg-[#FFB72B]" />
              {t("portfolio.freshness.status_data_temporarily_stale")}
            </span>
          ) : null}
          {reviewMeta && reviewMeta.ai_review_status === "market_data_unavailable" ? (
            <span
              className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-medium border border-[#FF7A45]/45 bg-[rgba(255,122,69,0.08)] text-[#FF7A45]"
              data-testid="portfolio-ai-review-status-market-data-unavailable"
              aria-label={t("portfolio.freshness.status_market_data_unavailable")}
            >
              <span className="h-1.5 w-1.5 rounded-full bg-[#FF7A45]" />
              {t("portfolio.freshness.status_market_data_unavailable")}
            </span>
          ) : null}
          {reviewMeta && reviewMeta.ai_review_status === "portfolio_changed_refresh_analysis" ? (
            <span
              className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-medium border border-[#3DEBFF]/45 bg-[rgba(61,235,255,0.08)] text-[#3DEBFF]"
              data-testid="portfolio-ai-review-status-portfolio-changed"
              aria-label={t("portfolio.freshness.status_portfolio_changed_refresh_analysis")}
            >
              <span className="h-1.5 w-1.5 rounded-full bg-[#3DEBFF]" />
              {t("portfolio.freshness.status_portfolio_changed_refresh_analysis")}
            </span>
          ) : null}
          {/* Legacy fallback: keep the pre-Phase-8.PH pill working when
              the backend hasn't populated `ai_review_status` yet. */}
          {reviewMeta && !reviewMeta.ai_review_status && reviewMeta.cached && !reviewMeta.is_stale ? (
            <span
              className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-medium border border-[#2BFF8A]/40 bg-[rgba(43,255,138,0.06)] text-[#2BFF8A]"
              data-testid="portfolio-ai-review-up-to-date"
            >
              <span className="h-1.5 w-1.5 rounded-full bg-[#2BFF8A]" />
              {t("portfolio.freshness.up_to_date")}
            </span>
          ) : null}
          {generatedAt ? (
            <div className="text-[11px] text-muted-foreground" data-testid="portfolio-ai-review-generated-at">
              {t("portfolio.freshness.updated_at", { date: new Date(generatedAt).toLocaleString() })}
            </div>
          ) : null}
        </div>
      </div>

      {/* Phase 8.B QA-4/QA-7/QA-10 — locale-mismatch banner. Fires
          whenever the persisted review locale differs from the active
          UI locale, regardless of whether the review came from cache,
          a fresh generation or a post-reload restore. The banner
          informs the user that the analysis is in a different language
          AND prompts them to refresh for the current language. No
          auto-refresh, no allowance consumed, no false badging. */}
      {localeMismatch ? (
        <div
          data-testid="portfolio-ai-review-locale-mismatch"
          className="mb-3 rounded-lg border border-[#3DEBFF]/40 bg-[rgba(61,235,255,0.06)] px-3 py-2 text-xs text-[#7CECFF]"
        >
          {normalizedReviewLocale === "de"
            ? t("portfolio.freshness.locale_mismatch_de")
            : t("portfolio.freshness.locale_mismatch_en")}
        </div>
      ) : null}

      {/* Phase 8.B — Stale-review banner. Old review stays visible. */}
      {reviewMeta && reviewMeta.cached && reviewMeta.is_stale ? (
        <div
          data-testid="portfolio-ai-review-stale-banner"
          className="mb-4 rounded-lg border border-[#FFB72B]/40 bg-[rgba(255,183,43,0.08)] px-3 py-2 text-xs"
        >
          <div className="font-medium text-[#FFB72B]">{t("portfolio.freshness.stale_title")}</div>
          <div className="text-foreground/80 mt-0.5">{t("portfolio.freshness.stale_body")}</div>
        </div>
      ) : null}

      {/* Summary */}
      {review.summary ? (
        <p
          className={`text-sm leading-relaxed mb-5 ${previewMode ? "[filter:blur(6px)] select-none pointer-events-none" : ""}`}
          data-testid="portfolio-ai-review-summary"
          aria-hidden={previewMode ? "true" : undefined}
        >
          {review.summary}
        </p>
      ) : null}

      {/* Strengths / Weaknesses */}
      <div className="grid md:grid-cols-2 gap-5 mb-5">
        {strengths.length ? (
          <div data-testid="portfolio-ai-review-strengths">
            <SectionHeader icon={Lightbulb} color="#2BFF8A" label={t("portfolio.review.strengths") || "Strengths"} testId="section-strengths-header" />
            <ul
              className={`text-sm space-y-1 list-disc list-inside text-foreground/90 ${previewMode ? "[filter:blur(6px)] select-none pointer-events-none" : ""}`}
              aria-hidden={previewMode ? "true" : undefined}
            >
              {strengths.map((s, i) => <li key={i}>{s}</li>)}
            </ul>
          </div>
        ) : null}
        {weaknesses.length ? (
          <div data-testid="portfolio-ai-review-weaknesses">
            <SectionHeader icon={ShieldAlert} color="#FF3DA6" label={t("portfolio.review.weaknesses") || "Weaknesses"} testId="section-weaknesses-header" />
            <ul
              className={`text-sm space-y-1 list-disc list-inside text-foreground/90 ${previewMode ? "[filter:blur(6px)] select-none pointer-events-none" : ""}`}
              aria-hidden={previewMode ? "true" : undefined}
            >
              {weaknesses.map((s, i) => <li key={i}>{s}</li>)}
            </ul>
          </div>
        ) : null}
      </div>

      {/* Concentration risks */}
      {concentrationRisks.length ? (
        <div className="mb-5" data-testid="portfolio-ai-review-concentration-risks">
          <SectionHeader icon={ShieldAlert} color="#FFB72B" label={t("portfolio.review.concentration") || "Concentration Risks"} testId="section-concentration-header" />
          <ul
            className={`space-y-2 ${previewMode ? "[filter:blur(6px)] select-none pointer-events-none" : ""}`}
            aria-hidden={previewMode ? "true" : undefined}
          >
            {concentrationRisks.map((c, i) => (
              <li key={i} className="flex items-start gap-2 text-sm" data-testid={`concentration-risk-${i}`}>
                <SeverityPill severity={c.severity} t={t} />
                <div>
                  <span className="font-medium text-foreground/95">{c.name}</span>
                  {c.explanation ? <span className="text-foreground/75"> — {c.explanation}</span> : null}
                </div>
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {/* Top opportunity / Largest valuation risk */}
      {(topOpportunity || largestRisk) ? (
        <div className="grid md:grid-cols-2 gap-4 mb-5">
          {topOpportunity ? (
            <div data-testid="portfolio-ai-review-top-opportunity">
              <SectionHeader icon={Lightbulb} color="#2BFF8A" label={t("portfolio.review.top_opportunity") || "Top Opportunity"} testId="section-top-opp-header" />
              <div className={`text-sm ${previewMode ? "[filter:blur(6px)] select-none pointer-events-none" : ""}`} aria-hidden={previewMode ? "true" : undefined}>
                {topOpportunity.ticker ? <span className="font-mono font-semibold text-[#2BFF8A] mr-1">{topOpportunity.ticker}</span> : null}
                <span className="text-foreground/85">{topOpportunity.explanation}</span>
              </div>
            </div>
          ) : null}
          {largestRisk ? (
            <div data-testid="portfolio-ai-review-largest-risk">
              <SectionHeader icon={ShieldAlert} color="#FF3DA6" label={t("portfolio.review.largest_risk") || "Largest Valuation Risk"} testId="section-largest-risk-header" />
              <div className={`text-sm ${previewMode ? "[filter:blur(6px)] select-none pointer-events-none" : ""}`} aria-hidden={previewMode ? "true" : undefined}>
                {largestRisk.ticker ? <span className="font-mono font-semibold text-[#FF3DA6] mr-1">{largestRisk.ticker}</span> : null}
                <span className="text-foreground/85">{largestRisk.explanation}</span>
              </div>
            </div>
          ) : null}
        </div>
      ) : null}

      {/* Research priorities */}
      {researchPriorities.length ? (
        <div className="mb-4" data-testid="portfolio-ai-review-research-priorities">
          <SectionHeader icon={Compass} color="#3DEBFF" label={t("portfolio.review.research_priorities") || "Research Priorities"} testId="section-research-header" />
          <ul
            className={`text-sm space-y-1 ${previewMode ? "[filter:blur(6px)] select-none pointer-events-none" : ""}`}
            aria-hidden={previewMode ? "true" : undefined}
          >
            {researchPriorities.map((p, i) => (
              <li key={i} className="flex items-start gap-1.5 text-foreground/90">
                <ChevronRight className="w-3.5 h-3.5 mt-0.5 text-[#3DEBFF]" />
                <span>{p}</span>
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {/* Confidence / footer */}
      {review.confidence ? (
        <div
          className="text-[11px] text-muted-foreground border-t border-white/5 pt-2 leading-relaxed"
          data-testid="portfolio-ai-review-confidence"
        >
          <strong className="text-foreground/70">{t("portfolio.review.confidence") || "Confidence"}:</strong>{" "}
          <span
            className={previewMode ? "[filter:blur(6px)] select-none pointer-events-none inline-block align-middle" : ""}
            aria-hidden={previewMode ? "true" : undefined}
          >
            {review.confidence}
          </span>
        </div>
      ) : null}
    </div>
  );
}
